import { useEffect, useState } from 'react';
import { differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns';

const formatDateExpiration = (endTime: Date) => {
  const now = new Date();
  const diffDays = differenceInDays(endTime, now);
  const diffHours = differenceInHours(endTime, now);
  const diffMinutes = differenceInMinutes(endTime, now);
  const diffSeconds = differenceInSeconds(endTime, now);

  if (diffSeconds < 0) {
    return '0s';
  }

  if (diffSeconds < 10) {
    return `${diffSeconds}s`;
  }

  if (diffMinutes < 1) {
    return `${diffSeconds}s`;
  }

  if (diffHours < 1) {
    const secondsLeft = diffSeconds - diffMinutes * 60;
    return `${diffMinutes}m ${secondsLeft}s`;
  }

  if (diffHours < 24) {
    const minutesLeft = diffMinutes - diffHours * 60;
    return `${diffHours}h ${minutesLeft}m`;
  }

  const hoursLeft = diffHours - diffDays * 24;
  return `${diffDays}d ${hoursLeft}h`;
};

const useCountdownInterval = (
  dateExpired: Date,
  {
    constant = false,
    formatFn,
  }: {
    constant?: boolean;
    formatFn?: (date: Date) => string;
  } = {},
) => {
  const formattedTimeLeft = formatFn ? formatFn(dateExpired) : formatDateExpiration(dateExpired);

  const [timeLeft, setTimeLeft] = useState(formattedTimeLeft);

  useEffect(() => {
    const hoursDiff = differenceInHours(dateExpired, new Date());

    let interval;
    if (hoursDiff < 1 || constant) {
      const updateTimer = () => {
        setTimeLeft(formatFn ? formatFn(dateExpired) : formatDateExpiration(dateExpired));
        const now = new Date().getTime();
        const nextSecond = Math.ceil(now / 1000) * 1000;
        const delay = nextSecond - now;
        return setTimeout(updateTimer, delay);
      };
      interval = updateTimer();
    } else {
      setTimeLeft(formatFn ? formatFn(dateExpired) : formatDateExpiration(dateExpired));
    }

    return () => {
      if (interval) {
        clearTimeout(interval);
      }
    };
  }, [dateExpired]);

  return { timeLeft };
};

export default useCountdownInterval;
